import type { AccesoDeInicio, AnidamientoNavegacion, EntradaNavegacion, GrupoNavegacion, Navegacion, ZonaNavegacion } from '../api/operaciones';

/**
 * Menú del panel a partir de `getMyNavigation` (G97, G128 a G130; ADR-0041, ADR-0051). El servidor decide **todo**: qué
 * entradas ve cada persona (permiso, bloques y modo), dónde va cada una (menú principal o perfil), su categoría (una de
 * las seis de G128), su icono como clave semántica, su orden (con el de la persona ya aplicado, G129), si la persona la
 * quitó a «Más» (`in_more`), si va dentro de otra (`parent_key`, G130) y la barra del móvil (`mobile_bar`). Una
 * categoría sin entradas no llega y la que tendría una sola ya llega dentro de Operativa: el panel no las recoloca. El
 * panel no guarda listas propias de entradas, categorías ni permisos (D-061). El icono queda como la clave semántica del
 * contrato (`NavigationIcon`), que el marco traduce a su dibujo, con uno genérico si no la conoce.
 *
 * Lo que el rol no puede ver no llega (sin candado, G120). Los bloques no contratados solo le llegan al Propietario, con
 * `not_contracted` y su `entitlement`: se pintan con «+» y llevan a la ruta de la entrada `plan`
 * con `?block=<entitlement>` (ADR-0047). El panel no sabe quién es el Propietario: lo decide el servidor.
 */
export interface EntradaMenu {
    clave: string;
    modulo: string;
    etiqueta: string;
    ruta: string;
    zona: ZonaNavegacion;
    grupo: GrupoNavegacion | null;
    /** Clave semántica del icono (`NavigationIcon`), tal como la manda el servidor. */
    icono: string;
    soloLectura: boolean;
    contador: number | null;
    /** Bloque que la activa (`entitlement`); null si no depende de ninguno. */
    bloque: string | null;
    /** Bloque sin contratar (solo le llega al Propietario): «+» y, al tocarla, la entrada `plan` con ese bloque. */
    noContratada: boolean;
    /** La persona la quitó del menú: va en «Más» (`in_more`, G129). */
    enMas: boolean;
    /** Entrada madre dentro de cuya pantalla se pinta esta (`parent_key`, G130); null en las del menú. */
    padre: string | null;
    /** Cómo se pinta dentro de la madre: apartado o tarjeta (`nested_as`, G130). */
    anidadaComo: AnidamientoNavegacion | null;
}

/** Clave de la entrada de plan y facturación (ficha del núcleo §13), adonde lleva el «+» de un bloque sin contratar. */
export const CLAVE_PLAN = 'plan';

/** Clave de la entrada de Inicio (ficha de Analítica), que nunca se quita del menú (G129). */
export const CLAVE_INICIO = 'inicio';

/**
 * Título de cada categoría del contrato (`NavigationGroup`, G128): el menú agrupa por la que mande el servidor, en el
 * orden en que llegan sus entradas, sin una lista fija; una categoría sin título conocido sale con su clave tal cual.
 */
const TITULO_GRUPO: Readonly<Record<GrupoNavegacion, string>> = {
    operations: 'Operativa',
    events: 'Eventos',
    communication: 'Comunicación',
    access: 'Acceso',
    tracking: 'Seguimiento',
    settings: 'Configuración',
};

export interface GrupoMenu {
    /** Clave de la categoría que manda el servidor. */
    clave: string;
    titulo: string;
    entradas: EntradaMenu[];
}

export function tituloGrupo(clave: string): string {
    return (TITULO_GRUPO as Readonly<Record<string, string>>)[clave] ?? clave;
}

export interface Menu {
    /** Menú lateral por categorías, en el orden de la API, sin las quitadas a «Más» (una categoría vacía no sale). */
    grupos: GrupoMenu[];
    /** Todas las del menú principal que son pantallas del menú (también las quitadas), en el orden de la API. */
    entradas: EntradaMenu[];
    /** Las que la persona quitó del menú, que van en «Más» (G129). */
    quitadas: EntradaMenu[];
    /** Las que se pintan dentro de otra (G130). */
    anidadas: EntradaMenu[];
    /** Menú del perfil, en el orden de la API. */
    perfil: EntradaMenu[];
    /** Claves de la barra inferior del móvil, en orden (`mobile_bar`, G97, G129). */
    barraMovil: string[];
    /** Ruta de la entrada `plan`, si la API la da a esta persona (G120). */
    rutaPlan: string | null;
}

export function entradaMenu(item: EntradaNavegacion): EntradaMenu {
    return {
        clave: item.key,
        modulo: item.module,
        etiqueta: item.label,
        ruta: item.path,
        zona: item.area,
        grupo: item.group ?? null,
        icono: item.icon,
        soloLectura: item.read_only === true,
        contador: item.badge ?? null,
        bloque: item.entitlement ?? null,
        noContratada: item.not_contracted,
        enMas: item.in_more,
        padre: item.parent_key ?? null,
        anidadaComo: item.nested_as ?? null,
    };
}

/** Agrupa por la categoría que manda el servidor, en el orden en que llegan (una categoría vacía no sale). */
export function agrupar(entradas: readonly EntradaMenu[]): GrupoMenu[] {
    const claves = [...new Set(entradas.map((e) => e.grupo ?? ''))];
    return claves.map((clave) => ({ clave, titulo: tituloGrupo(clave), entradas: entradas.filter((e) => (e.grupo ?? '') === clave) }));
}

export function construirMenu(navegacion: Pick<Navegacion, 'items'> & Partial<Pick<Navegacion, 'mobile_bar'>>): Menu {
    const todas = navegacion.items.map(entradaMenu);
    const delMenu = todas.filter((e) => e.zona === 'menu');
    const entradas = delMenu.filter((e) => e.padre === null);
    return {
        grupos: agrupar(entradas.filter((e) => !e.enMas)),
        entradas,
        quitadas: entradas.filter((e) => e.enMas),
        anidadas: delMenu.filter((e) => e.padre !== null),
        perfil: todas.filter((e) => e.zona === 'profile'),
        barraMovil: navegacion.mobile_bar ?? [],
        rutaPlan: todas.find((e) => e.clave === CLAVE_PLAN)?.ruta ?? null,
    };
}

/**
 * A dónde lleva una referencia de un aviso (`GenericRef`: tipo e id) con los destinos que da `getMyNavigation`
 * (`link_targets`, G298): la ruta del panel con su id y la etiqueta, la del tipo de aviso si la trae (`link_label`) o la
 * del destino. Si el tipo no llega (la persona no puede ver esa pantalla), no hay enlace (null). El panel no conoce rutas
 * de otros módulos.
 */
export function enlaceDeReferencia(destinos: Navegacion['link_targets'], tipo: string, id: string, etiqueta?: string | null): { ruta: string; etiqueta: string } | null {
    const destino = destinos.find((d) => d.ref_type === tipo);
    return destino === undefined ? null : { ruta: destino.path.replaceAll('{id}', encodeURIComponent(id)), etiqueta: etiqueta ?? destino.label };
}

/** Las que se pintan dentro de la pantalla de una entrada (G130): apartados y tarjetas, en el orden de la API. */
export function anidadasDe(menu: Pick<Menu, 'anidadas'>, clave: string): EntradaMenu[] {
    return menu.anidadas.filter((e) => e.padre === clave);
}

/**
 * Adónde lleva tocar una entrada: su ruta o, si su bloque no está contratado, la entrada `plan` con ese bloque
 * (`?block=<entitlement>`, G120). Sin la entrada `plan` en el menú de esta persona, a ninguna parte (null).
 */
export function destinoDe(menu: Pick<Menu, 'rutaPlan'>, entrada: Pick<EntradaMenu, 'ruta' | 'bloque' | 'noContratada'>): string | null {
    if (!entrada.noContratada) {
        return entrada.ruta;
    }
    if (menu.rutaPlan === null || entrada.bloque === null) {
        return null;
    }
    return `${menu.rutaPlan}?${new URLSearchParams({ block: entrada.bloque }).toString()}`;
}

/**
 * Pantalla que corresponde a una ruta: del menú, del perfil o ninguna (la API no se la da a esta persona). La ruta de
 * una entrada que va dentro de otra (G130) es la pantalla de su madre, con ese `apartado` abierto.
 */
export type Pantalla = { tipo: 'menu' | 'perfil'; entrada: EntradaMenu; apartado?: EntradaMenu } | { tipo: 'desconocida'; ruta: string };

export function pantallaDe(menu: Menu, ruta: string): Pantalla {
    const limpia = ruta.replace(/\/+$/, '') || '/';
    // Una entrada de un bloque sin contratar no es una pantalla: su «+» lleva a la entrada `plan`.
    const delMenu = menu.entradas.find((e) => e.ruta === limpia && !e.noContratada);
    if (delMenu !== undefined) {
        return { tipo: 'menu', entrada: delMenu };
    }
    const delPerfil = menu.perfil.find((e) => e.ruta === limpia);
    if (delPerfil !== undefined) {
        return { tipo: 'perfil', entrada: delPerfil };
    }
    const anidada = menu.anidadas.find((e) => e.ruta === limpia && !e.noContratada);
    const madre = anidada === undefined ? undefined : menu.entradas.find((e) => e.clave === anidada.padre);
    if (anidada !== undefined && madre !== undefined) {
        return { tipo: 'menu', entrada: madre, apartado: anidada };
    }
    return { tipo: 'desconocida', ruta: limpia };
}

/** Ruta de entrada al panel: la primera del menú (contratada) o, si no tiene ninguna, la primera del perfil. */
export function rutaInicial(menu: Menu): string | null {
    return menu.entradas.find((e) => !e.noContratada)?.ruta ?? menu.perfil[0]?.ruta ?? null;
}

/**
 * «Administrador · <dominio>/sala-anden»: roles y dirección web del negocio, para distinguirlos (G99). La dirección
 * es `public_url` de getMe, que calcula el servidor con el dominio de cada entorno (G118): el panel no conoce dominios y
 * solo le quita el `https://` para escribirla como el diseño (0.1 y 1.4).
 */
export function lineaNegocio(rol: string, direccion: string): string {
    return `${rol} · ${direccion.replace(/^https?:\/\//, '')}`;
}

/** Iniciales para el avatar: primera letra de la primera y de la última palabra («Chiringuito El Pez» → «CP»). */
export function iniciales(nombre: string): string {
    const palabras = nombre.trim().split(/\s+/).filter((p) => p !== '');
    const primera = palabras[0] ?? '';
    const ultima = palabras.length > 1 ? (palabras[palabras.length - 1] ?? '') : '';
    return (primera.charAt(0) + ultima.charAt(0)).toLocaleUpperCase('es');
}

/**
 * Barra inferior del móvil (< 1024): las entradas de `mobile_bar` que manda el servidor (las que eligió la persona o, si
 * no eligió, las cuatro primeras de Operativa; G97, G129), sin las de «+», que no son pantallas, y «Más» con todo lo
 * demás por categorías y en el orden de la API. Sin listas propias: lo que se ve depende del rol y de los bloques.
 */
export interface BarraInferior {
    barra: EntradaMenu[];
    /** Lo que va en «Más», por categorías (una categoría sin entradas no sale). */
    mas: GrupoMenu[];
}

export function barraInferior(menu: Pick<Menu, 'entradas' | 'barraMovil'>): BarraInferior {
    const barra = menu.barraMovil.flatMap((clave) => menu.entradas.filter((e) => e.clave === clave && !e.noContratada)).slice(0, 4);
    return { barra, mas: agrupar(menu.entradas.filter((e) => !barra.includes(e))) };
}

/** En el menú lateral se pliegan todas las categorías menos la primera, «Operativa» (0.1, §1g). */
export function grupoPlegable(menu: Pick<Menu, 'grupos'>, clave: string): boolean {
    return menu.grupos[0]?.clave !== clave;
}

/** Por defecto, todas abiertas menos «Configuración» (0.1, §1g; 20 oct). */
export function grupoAbiertoPorDefecto(clave: string): boolean {
    return clave !== 'settings';
}

/**
 * «Tus accesos» de Inicio (G129): los de `home_shortcuts`, en su orden, de entre los `shortcuts` que el servidor da a
 * esta persona (filtrados por permiso, bloque y modo). Una clave que ya no llega no sale.
 */
export function accesosDeInicio(navegacion: Pick<Navegacion, 'shortcuts' | 'home_shortcuts'>): AccesoDeInicio[] {
    return navegacion.home_shortcuts.flatMap((clave) => navegacion.shortcuts.filter((a) => a.key === clave));
}

/**
 * Adónde lleva un acceso de Inicio (G963, ADR-0244): su ruta o, si su bloque no está contratado (`not_contracted`, solo le
 * llega al Propietario), la entrada `plan` con ese bloque (`?block=<entitlement>`), como las entradas del menú (G120).
 */
export function destinoDeAcceso(navegacion: Pick<Navegacion, 'items'>, acceso: AccesoDeInicio): string | null {
    if (!acceso.not_contracted) {
        return acceso.path;
    }
    return destinoDe(construirMenu(navegacion), {
        ruta: acceso.path,
        bloque: acceso.entitlement ?? null,
        noContratada: acceso.not_contracted,
    });
}
